import { useState, useRef, useEffect } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/components/wpl/image/src";
import { Link } from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import { Initializer } from "../../../../analytics/atl-analytics/initializer";

export interface StyledOuterImageProps {
    outerImageWidth: string;
    logoImageSrc: string;
    logoImageAlt: string;
}

const outerCardStyles = css({
    position: "relative",
    display: "flex",
    margin: "auto",
    maxWidth: "1000px",
    width: "100%",
    borderRadius: "3px",
    boxShadow: "1px 1px 6px -1px #97a0af",
    overflow: "hidden"
});

const cardStyles = css({
    display: "flex",
    width: "100%",

    [media.above.sm]: {
        flexDirection: "row"
    },

    [media.below.sm]: {
        flexDirection: "column"
    }
});

const cardSlideInFromLeftAnimation = keyframes({
    from: {
        marginLeft: "-100%"
    },

    to: {
        marginLeft: "0%"
    }
});

const cardSlideInFromRightAnimation = keyframes({
    from: {
        marginLeft: "100%"
    },

    to: {
        marginLeft: "0%"
    }
});

const cardSlideOutToLeftAnimation = keyframes({
    from: {
        marginLeft: "0%"
    },

    to: {
        marginLeft: "-100%",
        display: "none"
    }
});

const cardSlideOutToRightAnimation = keyframes({
    from: {
        marginLeft: "0%"
    },

    to: {
        marginLeft: "100%",
        display: "none"
    }
});

const SlidingInTestimonialCard = ({
    testimonialCardProps,
    slideRight
}: SlidingTestimonialCardProps) => {
    return (
        <div
            css={[
                css({
                    position: "relative",
                    display: "flex",
                    width: "100%"
                }),
                slideRight &&
                    css({
                        animation: `${cardSlideInFromRightAnimation} 350ms ease forwards`
                    }),
                !slideRight &&
                    css({
                        animation: `${cardSlideInFromLeftAnimation} 350ms ease forwards`
                    })
            ]}
        >
            <TestimonialCard {...testimonialCardProps} />
        </div>
    );
};

const SlidingOutTestimonialCard = ({
    testimonialCardProps,
    slideRight
}: SlidingTestimonialCardProps) => {
    return (
        <div
            css={[
                css({
                    position: "absolute",
                    insetBlockStart: "0px",
                    insetInlineStart: "0px",
                    display: "flex",
                    width: "100%"
                }),
                slideRight &&
                    css({
                        animation: `${cardSlideOutToLeftAnimation} 350ms ease forwards`
                    }),
                !slideRight &&
                    css({
                        animation: `${cardSlideOutToRightAnimation} 350ms ease forwards`
                    })
            ]}
        >
            <TestimonialCard {...testimonialCardProps} />
        </div>
    );
};

export const StyledOuterImage = ({
    outerImageWidth,
    logoImageSrc,
    logoImageAlt
}: StyledOuterImageProps) => {
    return (
        <div
            css={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#eae6ff",
                padding: token("space.500"),

                [media.below.sm]: {
                    height: "250px",
                    flexBasis: "unset"
                },
                [media.above.sm]: {
                    flexBasis: "33.33%"
                },

                img: {
                    margin: "0",

                    [media.below.sm]: {
                        width: `${outerImageWidth}`,
                        maxWidth: "100%"
                    },

                    [media.above.sm]: {
                        maxWidth: "100%"
                    }
                }
            })}
        >
            <Image src={logoImageSrc} alt={logoImageAlt} aspectRatio="none" />
        </div>
    );
};

const buttonStyles = css({
    zIndex: "1",
    display: "inline-flex",
    position: "absolute",
    insetInlineEnd: "30px",
    insetBlockStart: "30px",

    button: {
        "marginInlineStart": "10px",
        "width": "30px",
        "height": "30px",
        "borderRadius": "5px",
        "border": "0",
        "boxShadow": "none",
        "backgroundColor": "#42526e",
        "color": "#ffffff",
        "fontFamily": `var(--bxp-charlie-text-font-family)`,
        "fontSize": "1rem",
        "textAlign": "center",
        "verticalAlign": "middle",

        "&:hover": {
            backgroundColor: "#505f79",
            cursor: "pointer"
        }
    }
});

const cardTextStyles = css({
    "padding": token("space.500"),
    "backgroundColor": "#ffffff",

    [media.above.sm]: {
        flex: "1"
    },

    ".testimonial-eyebrow-text": {
        color: "#505f79",
        fontSize: "0.75rem",
        fontWeight: "400",
        letterSpacing: "0.1rem",
        textTransform: "uppercase",
        marginBlockStart: "0",
        marginBlockEnd: "26px"
    },

    ".testimonial-quote-text": {
        color: "#172b4d",
        fontSize: "1.5rem",
        fontWeight: "400",
        lineHeight: "1.33333333",
        marginBlockStart: "0",
        marginBlockEnd: token("space.200"),
        letterSpacing: "0.3px",

        [media.below.sm]: {
            fontSize: "1.25rem",
            lineHeight: "1.2",
            marginBlockEnd: "14px"
        }
    },

    ".testimonial-quote-credit": {
        color: "#42526e",
        fontSize: "0.875rem",
        fontWeight: "700",
        letterSpacing: "1px",
        lineHeight: "1.25",
        textTransform: "uppercase",
        marginBlockStart: "0",
        marginBlockEnd: "14px",

        [media.above.md]: {
            marginBlockEnd: "24px"
        }
    },

    ".testimonial-quote-description": {
        marginBlockEnd: token("space.300")
    }
});

interface TestimonialCardProps {
    cardNumber: string;
    logoImageLink: string;
    logoImageAltText: string;
    imageWidth: string;
    quoteText: string;
    quoteCredit: string;
    quoteDescription: string;
    articleLink: string;
}

interface SlidingTestimonialCardProps {
    testimonialCardProps: TestimonialCardProps;
    slideRight: boolean;
}

const TestimonialCard = ({
    cardNumber,
    logoImageLink,
    logoImageAltText,
    imageWidth,
    quoteText,
    quoteCredit,
    quoteDescription,
    articleLink
}: TestimonialCardProps) => {
    const intl = useIntl();
    const linkText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.testimonialsSection.storyLink",
        defaultMessage: "Read the story"
    });

    const testimonialCardDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "testimonialsSection",
        "data-label": linkText,
        "data-event": "clicked"
    };
    const outerImageStyleProps: StyledOuterImageProps = {
        outerImageWidth: imageWidth,
        logoImageSrc: logoImageLink,
        logoImageAlt: logoImageAltText
    };

    return (
        <div css={cardStyles}>
            <StyledOuterImage {...outerImageStyleProps} />
            <div css={cardTextStyles}>
                <h6 className="testimonial-eyebrow-text">{cardNumber}</h6>
                <h3 className="testimonial-quote-text">{quoteText}</h3>
                <h5 className="testimonial-quote-credit">{quoteCredit}</h5>
                <div className="testimonial-quote-description">
                    {quoteDescription}
                </div>
                <Link
                    text={linkText}
                    customLinkColor="#0052cc"
                    doesArrowAnimate={true}
                    type="basic-underline"
                    href={articleLink}
                    dataEventProps={testimonialCardDataEventProps}
                />
            </div>
        </div>
    );
};

export const TestimonialCardsSection = () => {
    const intl = useIntl();
    const [currentCard, setCurrentCard] = useState(0);
    const prevCardRef = useRef<number>(0);
    useEffect(() => {
        prevCardRef.current = currentCard;

        Initializer.autoTrack();
    }, [currentCard]);

    const cards: TestimonialCardProps[] = [
        {
            cardNumber: "1/3",
            logoImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:31d0af93-6279-456e-ba48-a75903141f5d/Logo_Redfin.svg?cdnVersion=1263",
            logoImageAltText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneImageText",
                defaultMessage: "redfin logo"
            }),
            imageWidth: "215px",
            quoteText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneQuote",
                defaultMessage:
                    "“Confluence is our linchpin for everything. ‘How do I set up my dev box?’ ‘How do I get my work going?’ Whatever they want to know, it’s all there.”"
            }),
            quoteCredit: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneCredit",
                defaultMessage: "Evan Lerer"
            }),
            quoteDescription: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneDescription",
                defaultMessage: "Director of Engineering, Redfin"
            }),
            articleLink: useLocalizedHref("/customers/redfin")
        },
        {
            cardNumber: "2/3",
            logoImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:0d45de9a-b159-4032-94fd-0ed76b263987/Logo_Hubspot.svg?cdnVersion=1263",
            logoImageAltText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoImageText",
                defaultMessage: "hubspot logo"
            }),
            imageWidth: "256px",
            quoteText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoQuote",
                defaultMessage:
                    "“Information is available to every person in the organization from their very first day. HubSpot Confluence pages are open for anyone to discover, read, and comment on.”"
            }),
            quoteCredit: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoCredit",
                defaultMessage: "Katie Burke"
            }),
            quoteDescription: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoDescription",
                defaultMessage: "Chief People Officer, Hubspot"
            }),
            articleLink:
                "//atlassian.com/blog/leadership/innovation-transparency-at-work-hubspot"
        },
        {
            cardNumber: "3/3",
            logoImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:411b2282-6772-4a99-ac9f-ed6f546c8e72/RTR_logo.png?cdnVersion=1265",
            logoImageAltText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeImageText",
                defaultMessage: "Rent the runway logo"
            }),
            imageWidth: "320px",
            quoteText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeQuote",
                defaultMessage:
                    '“Confluence has greatly helped with requirements-gathering by allowing easy collaboration with much-appreciated version control, embedding of media, adding comments, and allowing flexible ways to organize and store documents."'
            }),
            quoteCredit: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeCredit",
                defaultMessage: "Jeannice Angela"
            }),
            quoteDescription: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeDescription",
                defaultMessage: "Product Manager, Rent the Runway"
            }),
            articleLink: useLocalizedHref("/customers/rent-the-runway")
        }
    ];

    const onClickLeftHandler = () => {
        setCurrentCard((currentCard + 2) % 3);
    };

    const onClickRightHandler = () => {
        setCurrentCard((currentCard + 1) % 3);
    };

    const testimonialSectionCarouselDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "carouselItem",
        "data-event-container": "testimonialsSection",
        "data-event": "clicked"
    };

    return (
        <div css={outerCardStyles}>
            <div css={buttonStyles}>
                <button
                    data-label={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.testimonialsSection.carouselLeft",
                        defaultMessage: "Carousel left click"
                    })}
                    {...testimonialSectionCarouselDataEventProps}
                    onClick={onClickLeftHandler}
                >
                    ←
                </button>
                <button
                    data-label={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.testimonialsSection.carouselRight",
                        defaultMessage: "Carousel right click"
                    })}
                    {...testimonialSectionCarouselDataEventProps}
                    onClick={onClickRightHandler}
                >
                    →
                </button>
            </div>
            {cards.map((cardProps: TestimonialCardProps, index: number) => {
                const toRight =
                    (prevCardRef.current === 0 && currentCard === 1) ||
                    (prevCardRef.current === 1 && currentCard === 2) ||
                    (prevCardRef.current === 2 && currentCard === 0);

                const isIntialRender =
                    prevCardRef.current === 0 && currentCard === 0;
                const slidingCardProps = {
                    testimonialCardProps: cardProps,
                    slideRight: toRight
                };
                return (
                    <div
                        key={`card-${index}`}
                        aria-hidden={index !== currentCard}
                    >
                        {isIntialRender && index === currentCard && (
                            <TestimonialCard {...cardProps} />
                        )}
                        {!isIntialRender && (
                            <>
                                {index === currentCard && (
                                    <SlidingInTestimonialCard
                                        {...slidingCardProps}
                                    />
                                )}
                                {index === prevCardRef.current && (
                                    <SlidingOutTestimonialCard
                                        {...slidingCardProps}
                                    />
                                )}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const TestimonialsSection = () => {
    return (
        <section
            css={css({
                padding: `${token("space.400")} ${token("space.200")}`,
                margin: "auto",

                [media.above.md]: {
                    width: "83.33333%"
                }
            })}
        >
            <TestimonialCardsSection />
        </section>
    );
};

export default TestimonialsSection;
